import { Injectable, inject } from '@angular/core';
import { ProviderEvents, Client, OpenFeature, EvaluationContext } from '@openfeature/angular-sdk';
import { Observable, BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  private logger = inject(NGXLogger);

  private openFeatureClient: Client;
  private readyPromise: Promise<void>;
  private readyResolver!: () => void;
  private featureFlagBooleanSubjects = new Map<string, BehaviorSubject<boolean>>();
  private featureFlagStringSubjects = new Map<string, BehaviorSubject<string>>();
  // TODO : Do the same for string and number flags

  constructor() {
    this.readyPromise = new Promise((resolve) => {
      this.readyResolver = resolve;
    });
    
    this.openFeatureClient = OpenFeature.getClient();
    this.openFeatureClient.addHandler(ProviderEvents.Ready, () => this.onReady());
    this.openFeatureClient.addHandler(ProviderEvents.ConfigurationChanged, () => this.onConfigurationChanged());
    this.openFeatureClient.addHandler(ProviderEvents.ContextChanged, () => this.onContextChanged());
    this.openFeatureClient.addHandler(ProviderEvents.Reconciling, () => this.onReconciling());
  }

  async isReady() {
    await this.readyPromise;
  }

  private async initializeBooleanFlag(key: string, defaultValue: boolean) {
    try {
      await this.readyPromise;
      const remoteValue = await this.openFeatureClient.getBooleanValue(key, defaultValue);
      const subject = this.featureFlagBooleanSubjects.get(key);
      if (subject && remoteValue !== subject.getValue()) {
        subject.next(remoteValue);
      }
    } catch (error) {
      this.logger.warn(`Error initializing boolean feature flag ${key}:`, error);
    }
  }

  private async initializeStringFlag(key: string, defaultValue: string) {
    try {
      await this.readyPromise;
      const remoteValue = await this.openFeatureClient.getStringValue(key, defaultValue);
      const subject = this.featureFlagStringSubjects.get(key);
      if (subject && remoteValue !== subject.getValue()) {
        subject.next(remoteValue);
      }
    } catch (error) {
      this.logger.warn(`Error initializing string feature flag ${key}:`, error);
    }
  }

  private onReconciling() {
    this.logger.log('OF Service Reconciling', new Date());
  }

  private onContextChanged() {
    this.logger.log('OF Service Context Changed', new Date());
  }

  private async refreshRemoteFlags() {
    for (const [key, subject] of this.featureFlagBooleanSubjects) {
      try {
        const value = await this.openFeatureClient.getBooleanValue(key, false);
        this.logger.log('OF Service Refreshing', key, value);
        if (value !== subject.getValue()) {
          this.logger.log('OF Service Refreshed', key, value);
          subject.next(value);
        }
      } catch (error) {
        this.logger.warn(`Error refreshing boolean feature flag ${key}:`, error);
      }
    }
    for (const [key, subject] of this.featureFlagStringSubjects) {
      try {
        const value = await this.openFeatureClient.getStringValue(key, '');
        this.logger.log('OF Service Refreshing', key, value);
        if (value !== subject.getValue()) {
          this.logger.log('OF Service Refreshed', key, value);
          subject.next(value);
        }
      } catch (error) {
        this.logger.warn(`Error refreshing string feature flag ${key}:`, error);
      }
    }
  }

  private onConfigurationChanged() {  
    // This is called when a feature flag has been changed
    this.logger.log('OF Service Config Changed', new Date());
    this.refreshRemoteFlags();
  }

  private onReady() {
    this.logger.log('OF Service Ready', new Date());
    this.logger.log('OF state', this.openFeatureClient.providerStatus, new Date());
    const context = OpenFeature.getContext();
    this.logger.log('OF context', context, new Date());
    this.readyResolver();
  }

  async getBooleanValue(feature: string, defaultValue: boolean): Promise<boolean> {
    await this.readyPromise;
    return this.openFeatureClient.getBooleanValue(feature, defaultValue);
  }

  async getStringValue(feature: string, defaultValue: string): Promise<string> {
    await this.readyPromise;
    return this.openFeatureClient.getStringValue(feature, defaultValue);
  }

  async getNumberValue(feature: string, defaultValue: number): Promise<number> {
    await this.readyPromise;
    return this.openFeatureClient.getNumberValue(feature, defaultValue);
  }

  getBooleanValue$(key: string, defaultValue: boolean): Observable<boolean> {
    // Create subject synchronously if it doesn't exist
    if (!this.featureFlagBooleanSubjects.has(key)) {
      const subject = new BehaviorSubject<boolean>(defaultValue);
      this.featureFlagBooleanSubjects.set(key, subject);
      // Initialize flag value asynchronously
      this.initializeBooleanFlag(key, defaultValue);
    }
    return this.featureFlagBooleanSubjects.get(key)!.asObservable().pipe(distinctUntilChanged());
  }

  getStringValue$(key: string, defaultValue: string): Observable<string> {
    if (!this.featureFlagStringSubjects.has(key)) {
      const subject = new BehaviorSubject<string>(defaultValue);
      this.featureFlagStringSubjects.set(key, subject);
      this.initializeStringFlag(key, defaultValue);
    }
    return this.featureFlagStringSubjects.get(key)!.asObservable().pipe(distinctUntilChanged());
  }

  getNumberValue$(feature: string, defaultValue: number): Observable<number> {
    const subject = new BehaviorSubject<number>(defaultValue);
    this.readyPromise.then(() => {
      subject.next(this.openFeatureClient.getNumberValue(feature, defaultValue));
    });
    return subject.pipe(distinctUntilChanged());
  }

  async setContext(context: EvaluationContext) {
    this.logger.log('OF Service Setting Context', context, new Date());
    return OpenFeature.setContext(context);
  }
}
